/** @format **/
import React, { FunctionComponent, ReactElement, useState } from 'react';
import MvnoBlockContain from '../../interfaces/platform/MvnoBlockContain';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { Checkbox, FormControlLabel, TextareaAutosize, Typography } from '@material-ui/core';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import CustomInputExtNumber from '../../components/CustomInput/CustomInputExtNumber';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { AlertDialog } from '../lib/dialog/AlertDialog';
import I18n from '../../components/Translate/I18n';

type MvnoBlockContainsJsonEditType = {
    mvnoBlockContains: MvnoBlockContain;
    reloadContainsList(): void;
};

export const MvnoBlockContainsJsonEdit: FunctionComponent<MvnoBlockContainsJsonEditType> = ({
    mvnoBlockContains,
    reloadContainsList,
}): ReactElement => {
    const dispatch = useDispatch();
    const [containsConditions, setContainsConditions] = useState(mvnoBlockContains.conditions ?? '');
    const [containsSleepSeconds, setContainsSleepSeconds] = useState<number>(mvnoBlockContains.sleepSeconds);
    const [containsPositionId, setContainsPositionId] = useState<number>(mvnoBlockContains.positionId);
    const [openAlert, setOpenAlert] = useState(false);
    const [skipSdgResponse, setSkipSdgResponse] = useState<boolean>(mvnoBlockContains.skipSdgResponse);
    const [skipMvnoBlock, setSkipMvnoBlock] = useState<boolean>(mvnoBlockContains.skipMvnoBlock);


    const onChangeEventTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const value = event.target.value;
        setContainsConditions(value);
    };

    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        switch (field) {
            case 'positionId':
                setContainsPositionId(parseInt(value));
                break;
            case 'sleepSeconds':
                setContainsSleepSeconds(parseInt(value));
                break;
            default:
                return;
        }
    };

    const savePosition = (): void => {
        mvnoBlockContains.positionId = containsPositionId;
        mvnoBlockContains.sleepSeconds = containsSleepSeconds;
        mvnoBlockContains.conditions = containsConditions;
        mvnoBlockContains.skipSdgResponse = skipSdgResponse;
        mvnoBlockContains.skipMvnoBlock = skipMvnoBlock;
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(mvnoBlockContains['@id']),
            'mvno_block_list_edit_contains',
            mvnoBlockContains,
            (data: MvnoBlockContain): void => {
                reloadContainsList();
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const deletePosition = (): void => {
        setOpenAlert(false);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.deleteCall(
            FormGeneralHelper.getByUri(mvnoBlockContains['@id']),
            'mvno_block_list_delete_contains',
            (data: MvnoBlockContain): void => {
                reloadContainsList();
            },
            {
                dispatchAfterCallback: false,
            }
        );
    };

    return (
        <GridContainer>
            <GridItem xs={12}>
                <Typography variant="h6">{mvnoBlockContains.mvnoBlockFunction.displayName}</Typography>
            </GridItem>
            <GridItem xs={4}>
                <GridContainer>
                    <GridItem xs={12}>
                        <CustomInputExtNumber
                            formControlProps={{ fullWidth: true }}
                            required={false}
                            labelText="prov:block.call_position_id"
                            name="positionId"
                            value={containsPositionId}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <CustomInputExtNumber
                            formControlProps={{ fullWidth: true }}
                            required={false}
                            labelText="prov:block.sleep_time_before_execute"
                            name="sleepSeconds"
                            value={containsSleepSeconds}
                            onChange={onChangeEvent}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem xs={8}>
                <TextareaAutosize
                    id="for-description"
                    style={{ width: '100%', resize: 'none' }}
                    name="description"
                    rowsMin={8}
                    rowsMax={8}
                    onChange={onChangeEventTextArea}
                    defaultValue={containsConditions}
                />
            </GridItem>

            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_BLOCK_CONTAINS_PUT) && (

                <>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={Boolean(skipSdgResponse)}
                        name={'skipSdgResponse'}
                        onChange={(): void => {
                            setSkipSdgResponse(
                                !skipSdgResponse
                            )
                        }}
                    />
                }
                label={<I18n trans={'prov:block.skip_sdg_response'} />}
            />

                    {mvnoBlockContains.mvnoBlockFunction.functionType === 'SET' && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(skipMvnoBlock)}
                                    name={'skipSdgResponse'}
                                    onChange={(): void => {
                                        setSkipMvnoBlock(
                                            !skipMvnoBlock
                                        )
                                    }}
                                />
                            }
                            label={<I18n trans={'prov:block.skip_mvno_response'} />}
                        />
                    )}


                </>
                )}
            <GridItem xs={12}>
                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_BLOCK_CONTAINS_PUT) && (
                    <CustomTextButtom callback={savePosition} callbackParam={{}} label="general:common.save" size="sm" />
                )}
                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_BLOCK_CONTAINS_DELETE) && (
                    <CustomTextButtom callback={(): void => setOpenAlert(true)} callbackParam={{}} label="general:common.delete" size="sm" />
                )}
                <AlertDialog
                    open={openAlert}
                    titleDialog="prov:block.confirm_deleted_position"
                    displayMessage="prov:block.confirm_deleted_position_with_id"
                    displayMessageValueObject={{ positionId: mvnoBlockContains.positionId }}
                    closeCallback={(): void => setOpenAlert(false)}
                    confirmCallback={deletePosition}
                />
            </GridItem>
            <GridItem xs={12}>
                <hr />
            </GridItem>
        </GridContainer>
    );
};
